import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { Clip, ClipStats } from '@/types/clip.types';
import {
  normalizeStatus,
  buildMergeSequence,
} from '@/utils/episode-helpers';
import {
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Mic,
} from 'lucide-react';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { cn } from '@/lib/utils';
import { getFtpStreamUrl } from '@/services/api';
import { AudioPlayerHandle } from '@/components/AudioPlayer';
import { StatusBanner } from '@/components/StatusBanner';
import { EpisodeNotFound } from '@/components/EpisodeNotFound';
import { ArticleSelectorModal } from '@/components/ArticleSelectorModal';
import { FeatureBanner } from '@/components/FeatureBanner';
import { EpisodeDetailSkeleton } from '@/components/EpisodeDetailSkeleton';
import { ActionBar } from '@/components/ActionBar';
import { EditEpisodeDetailsDialog } from '@/components/EditEpisodeDetailsDialog';
import { EpisodeHeaderCard } from '@/components/EpisodeHeaderCard';
import { EpisodeStatusContent } from '@/components/EpisodeStatusContent';
import { PublishEpisodeDialog } from '@/components/PublishEpisodeDialog';
import { ConfirmationDialog, ConfirmationDialogConfig } from '@/components/ConfirmationDialog';
import PresenceIndicator from '@/components/PresenceIndicator';
// @ts-ignore - Module Federation dynamic import
import { auth } from 'shell/Firebase';
import { useHelpContextForStatus } from '@/components/help-widget';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { usePlaybackController } from '@/hooks/usePlaybackController';
import { useEpisodeRealtime } from '@/hooks/useEpisodeRealtime';
import { useEpisodeActions } from '@/hooks/useEpisodeActions';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileEpisodeDetail } from '@/components/mobile';

type DialogId =
  | 'archive' | 'delete' | 'generateScripts' | 'approveScripts'
  | 'approveAudio' | 'merge' | 'generateAudio' | 'retryAudio'
  | 'retryScripts' | 'retryAll' | 'unarchive';

export const EpisodeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  const hPad = breakpoint === 'desktop' ? '32px' : '16px';
  // Get user companies from context (passed from shell via window event)
  const { userCompanies, getCompanyName } = useUserCompanies();
  // Memoize companyIds to prevent infinite re-renders when used in useCallback dependencies
  const companyIds = useMemo(
    () => userCompanies.length > 0 ? userCompanies.map(c => c.id) : undefined,
    [userCompanies]
  );
  const [episode, setEpisode] = useState<PodcastEpisode | null>(null);
  const [clips, setClips] = useState<Clip[]>([]);
  const [, setClipStats] = useState<ClipStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDialog, setActiveDialog] = useState<DialogId | null>(null);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showArticleSelector, setShowArticleSelector] = useState(false);
  const [clipsLoading, setClipsLoading] = useState(true);

  // Title state (for syncing with dialog)
  const [, setEditedTitle] = useState('');

  // Description editing state
  const [, setDescription] = useState('');
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false);
  const [isEditorExpanded, setIsEditorExpanded] = useState(false);

  // Publish time presets from company settings
  const [publishTimePresets, setPublishTimePresets] = useState<number[]>([]);

  // Duration tracking for merge preview
  const [totalDuration, setTotalDuration] = useState<number>(0);

  // Merged episode audio player state (URL loading — not playback)
  const [mergedAudioUrl, setMergedAudioUrl] = useState<string | null>(null);
  const [mergedAudioLoading, setMergedAudioLoading] = useState(false);
  const [mergedAudioError, setMergedAudioError] = useState<string | null>(null);

  // Artwork URL state (handles conversion from FTP path to stream URL)
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null);

  // Debounce ref for loadClips to prevent rapid API calls from WebSocket updates
  const loadClipsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ref for merged audio player to enable direct play control
  const mergedAudioRef = useRef<AudioPlayerHandle>(null);

  // Playback controller — manages all playback modes
  const {
    isPlayingAll, playAllIndex, isPlayAllPaused,
    handlePlayAll, handleStopPlayAll, handlePausePlayAll, handleResumePlayAll,
    handleNextSegment, handleSegmentEnded,
    isPlayingAllClips, playAllClipsIndex, isPlayAllClipsPaused,
    handlePlayAllClips, handleStopPlayAllClips, handlePausePlayAllClips, handleResumePlayAllClips,
    handleNextClip, handleClipEnded,
    currentlyPlayingId, handlePlayStateChange,
    isPlayingMerged, setIsPlayingMerged, handlePlayMerged, handlePauseMerged,
  } = usePlaybackController({ clips, mergePreview: episode?.mergePreview, mergedAudioRef });

  // Set help context based on episode status
  useHelpContextForStatus(episode?.status ?? null);

  // Breadcrumb: set initial items before paint, update with title when loaded
  const { setItems: setBreadcrumbItems, setRightContent: setBreadcrumbRight } = useBreadcrumb();
  useLayoutEffect(() => {
    setBreadcrumbItems([
      { label: 'Episode Composer', href: '../dashboard', icon: <Mic style={{ width: 14, height: 14, strokeWidth: 1.75 }} /> },
      { label: 'Episodes', href: '../episodes' },
    ]);
    return () => { setBreadcrumbItems([]); setBreadcrumbRight(null); };
  }, [setBreadcrumbItems, setBreadcrumbRight]);

  // Update breadcrumb with episode title once loaded
  useEffect(() => {
    if (episode) {
      setBreadcrumbItems([
        { label: 'Episode Composer', href: '../dashboard', icon: <Mic style={{ width: 14, height: 14, strokeWidth: 1.75 }} /> },
        { label: 'Episodes', href: '../episodes' },
        { label: episode.title },
      ]);
    }
  }, [episode, setBreadcrumbItems]);

  // Load clips and stats (internal function)
  const loadClipsInternal = useCallback(async () => {
    if (!id) return;
    try {
      const [clipsData, statsData] = await Promise.all([
        PodcastEpisodeService.getClips(id),
        PodcastEpisodeService.getClipStats(id),
      ]);
      // Sort clips by position
      const sortedClips = [...clipsData].sort((a, b) => a.position - b.position);
      setClips(sortedClips);
      setClipStats(statsData);
      // Note: Cover art sync is handled by the useEffect that watches clips changes
    } catch (err) {
      console.error('Failed to load clips:', err);
    } finally {
      setClipsLoading(false);
    }
  }, [id]);

  // Debounced loadClips - waits 2000ms before executing to batch rapid updates
  // during bulk operations (increased from 500ms to reduce API calls)
  const loadClips = useCallback(() => {
    if (loadClipsTimeoutRef.current) {
      clearTimeout(loadClipsTimeoutRef.current);
    }
    loadClipsTimeoutRef.current = setTimeout(() => {
      loadClipsInternal();
      loadClipsTimeoutRef.current = null;
    }, 2000);
  }, [loadClipsInternal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadClipsTimeoutRef.current) {
        clearTimeout(loadClipsTimeoutRef.current);
      }
    };
  }, []);

  // Realtime: WebSocket subscription, presence, episode/clip updates
  const {
    activeViewers,
    isLockedByOther,
    lockHolder,
    lockStateChanged,
    setInitialStatus,
  } = useEpisodeRealtime({
    episodeId: id,
    setEpisode,
    setClips,
    setError,
    loadClips,
    episodeTitle: episode?.title,
  });

  // Update breadcrumb right content with presence indicator
  useEffect(() => {
    setBreadcrumbRight(
      <PresenceIndicator
        viewers={activeViewers}
        currentUserId={auth.currentUser?.uid}
        lockedByUserId={activeViewers[0]?.odId}
      />
    );
  }, [activeViewers, setBreadcrumbRight]);

  // Episode actions — all mutation handlers
  const {
    actionInProgress, refreshEpisode,
    handleArchive, handleUnarchive, handleDelete,
    handleDeleteClip, handleMoveClip, saveScriptForClip,
    handleGenerateScripts, handleApproveScripts, handleRegenerateScript,
    handleGenerateAudio, handleApproveAudio, handleRegenerateAudio,
    handleMergeEpisode, handleRetryMerge,
    handleRetryFailedAudio, handleRetryFailedScripts, handleRetryAllFailed,
    handlePublish, handleSaveEpisodeDetails, handleArticlesAdded,
  } = useEpisodeActions({
    episodeId: id,
    episode,
    clips,
    setEpisode,
    setClips,
    setError,
    setClipsLoading,
    loadClips,
    setEditedTitle,
    setDescription,
  });

  // Close all dialogs when lock state changes to locked
  useEffect(() => {
    if (lockStateChanged > 0) {
      setActiveDialog(null);
      setShowPublishDialog(false);
      setShowArticleSelector(false);
      setShowEditDetailsDialog(false);
      setIsEditorExpanded(false);
    }
  }, [lockStateChanged]);

  // Fetch the episode on mount
  useEffect(() => {
    if (!id) return;

    const loadEpisode = async () => {
      setLoading(true);
      try {
        // Pass company IDs for multi-company lookup
        const ep = await PodcastEpisodeService.getEpisode(id, companyIds);
        if (ep) {
          const normalizedStatus = normalizeStatus(ep.status as string);
          setEpisode({ ...ep, status: normalizedStatus });
          setInitialStatus(normalizedStatus);
          setEditedTitle(ep.title);
          setDescription(ep.description || '');
        } else {
          setError('Episode not found');
        }
      } catch (err) {
        console.error('Error fetching episode:', err);
        setError(err instanceof Error ? err.message : 'Failed to load episode');
      } finally {
        setLoading(false);
      }
    };

    loadEpisode();
    loadClipsInternal(); // Use non-debounced version for initial load
  }, [id, loadClipsInternal, companyIds, setInitialStatus]);

  // Fetch publish time presets when episode is ready to publish (MERGED status)
  useEffect(() => {
    const canPublish = episode?.status === EpisodeStatus.MERGED ||
                       episode?.status === EpisodeStatus.PUBLISHING_FAILED;
    if (canPublish && publishTimePresets.length === 0) {
      PodcastEpisodeService.getPublishTimePresets()
        .then((presets) => {
          if (presets.length > 0) {
            setPublishTimePresets(presets);
          }
        })
        .catch((err) => {
          // Silently fail - will use defaults in dialog
          console.warn('Failed to load publish time presets:', err);
        });
    }
  }, [episode?.status, publishTimePresets.length]);

  // Calculate total duration from merge sequence (clips + intro/outro/commercials)
  useEffect(() => {
    if (!episode?.mergePreview) {
      setTotalDuration(0);
      return;
    }

    const mergeSequence = buildMergeSequence(clips, episode.mergePreview);
    const total = mergeSequence.reduce((sum, item) => {
      return sum + (item.duration || 0);
    }, 0);
    setTotalDuration(total);
  }, [clips, episode?.mergePreview]);

  // Load merged audio URL when episode is merged or published
  useEffect(() => {
    const loadMergedAudioUrl = async () => {
      // Only load for merged, publishing, or published status
      const statusesWithMergedAudio = [
        EpisodeStatus.MERGED,
        EpisodeStatus.PUBLISHING,
        EpisodeStatus.PUBLISHED,
      ];
      if (!episode || !statusesWithMergedAudio.includes(episode.status)) return;

      // Load audio from FTP for all merged/publishing/published statuses
      // (Podbean mediaUrl causes CORS issues, but the file remains on FTP)
      if (!episode.mergedAudioPath) {
        setMergedAudioError('No audio path available. Try re-merging the episode.');
        return;
      }

      setMergedAudioLoading(true);
      setMergedAudioError(null);

      try {
        const url = await getFtpStreamUrl(episode.mergedAudioPath, episode.companyId);
        setMergedAudioUrl(url);
      } catch (err) {
        console.error('Failed to get merged audio URL:', err);
        setMergedAudioError('Failed to load audio');
      } finally {
        setMergedAudioLoading(false);
      }
    };

    loadMergedAudioUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.mergedAudioPath, episode?.mediaUrl, episode?.status, episode?.companyId]);

  // Convert coverArtUrl to displayable URL (handles both public URLs and FTP paths)
  useEffect(() => {
    const loadArtworkUrl = async () => {
      if (!episode?.coverArtUrl) {
        setArtworkUrl(null);
        return;
      }

      // Check if it's already a public URL (http/https)
      if (episode.coverArtUrl.startsWith('http://') || episode.coverArtUrl.startsWith('https://')) {
        setArtworkUrl(episode.coverArtUrl);
        return;
      }

      // It's an FTP path - convert to stream URL
      try {
        const url = await getFtpStreamUrl(episode.coverArtUrl, episode.companyId);
        setArtworkUrl(url);
      } catch (err) {
        console.error('Failed to get artwork URL:', err);
        setArtworkUrl(null);
      }
    };

    loadArtworkUrl();
  }, [episode?.coverArtUrl, episode?.companyId]);

  // Ref to measure the content area's offset from viewport top for scroll containment
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const [contentMaxHeight, setContentMaxHeight] = useState<string>('auto');

  // Check if all scripts are ready (for hiding animation when done)
  const allScriptsReady = clips.length > 0 && clips.every((c) =>
    ['script_ready', 'script_approved', 'audio_queued', 'generating_audio', 'audio_ready', 'audio_approved'].includes(c.status)
  );

  // Check if all audio clips are ready (for hiding animation when done)
  const allAudioReady = clips.length > 0 && clips.every((c) =>
    ['audio_ready', 'audio_approved'].includes(c.status)
  );

  // Measure offset and set maxHeight so only clips area scrolls (not the whole page)
  useEffect(() => {
    const measure = () => {
      if (contentAreaRef.current) {
        const rect = contentAreaRef.current.getBoundingClientRect();
        setContentMaxHeight(`calc(100vh - ${Math.round(rect.top)}px)`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [episode?.status, clips.length, isEditorExpanded, allScriptsReady, allAudioReady]);

  // Handle secondary actions from ActionBar
  const handleSecondaryAction = (action: string) => {
    if (action === 'Play All') {
      if (episode?.status === EpisodeStatus.AUDIO_PENDING_REVIEW) {
        handlePlayAllClips();
      } else {
        handlePlayAll();
      }
      return;
    }
    if (isLockedByOther) return;
    switch (action) {
      case 'Add Articles':
        setShowArticleSelector(true);
        break;
      case 'Re-merge':
        handleRetryMerge();
        break;
    }
  };

  const isPublished = episode?.status === EpisodeStatus.PUBLISHED;

  const handleOpenEditDetailsDialog = () => {
    if (isPublished || isLockedByOther) return;
    setIsEditorExpanded(prev => !prev);
  };

  // Primary action handler for ActionBar - dispatches based on episode status
  const handlePrimaryAction = () => {
    if (!episode || isLockedByOther) return;
    switch (episode.status) {
      case EpisodeStatus.DRAFT:
        setActiveDialog('generateScripts');
        break;
      case EpisodeStatus.SCRIPTS_PENDING_REVIEW:
        setActiveDialog('approveScripts');
        break;
      case EpisodeStatus.SCRIPTS_APPROVED:
        setActiveDialog('generateAudio');
        break;
      case EpisodeStatus.AUDIO_PENDING_REVIEW:
        setActiveDialog('approveAudio');
        break;
      case EpisodeStatus.AUDIO_APPROVED:
        setActiveDialog('merge');
        break;
      case EpisodeStatus.MERGED:
      case EpisodeStatus.PUBLISHING_FAILED:
        setShowPublishDialog(true);
        break;
      case EpisodeStatus.AUDIO_FAILED:
        setActiveDialog('retryAudio');
        break;
      case EpisodeStatus.SCRIPT_FAILED:
        setActiveDialog('retryScripts');
        break;
      case EpisodeStatus.FAILED:
        setActiveDialog('retryAll');
        break;
      case EpisodeStatus.ARCHIVED:
        setActiveDialog('unarchive');
        break;
    }
  };

  // Dialog configurations — one entry per confirmation dialog
  const dialogConfigs: ConfirmationDialogConfig[] = [
    { id: 'archive', title: 'Archive Episode', description: 'Are you sure you want to archive this episode? Archived episodes can be deleted permanently.', confirmLabel: 'Archive', loadingLabel: 'Archiving...', actionKey: 'archive', onConfirm: handleArchive },
    { id: 'delete', title: 'Delete Episode', description: 'Are you sure you want to permanently delete this episode? This action cannot be undone.', confirmLabel: 'Delete', loadingLabel: 'Deleting...', actionKey: 'delete', variant: 'destructive', onConfirm: handleDelete },
    { id: 'generateScripts', title: 'Generate Scripts', description: 'This will generate AI scripts for all clips. You can review and edit them before generating audio.', confirmLabel: 'Generate Scripts', loadingLabel: 'Generating...', actionKey: 'generate-scripts', onConfirm: handleGenerateScripts },
    { id: 'approveScripts', title: 'Approve All Scripts', description: 'This will approve all scripts and advance the episode to audio generation. Make sure you\'ve reviewed the scripts before proceeding.', confirmLabel: 'Approve All', loadingLabel: 'Approving...', actionKey: 'approve-scripts', onConfirm: handleApproveScripts },
    { id: 'approveAudio', title: 'Approve All Audio', description: 'This will approve all audio clips and advance the episode to the next stage. Continue?', confirmLabel: 'Approve All', loadingLabel: 'Approving...', actionKey: 'approve-audio', onConfirm: handleApproveAudio },
    { id: 'merge', title: 'Merge Episode', description: 'This will combine all audio clips into the final episode file. This process may take a few minutes.', confirmLabel: 'Merge', loadingLabel: 'Merging...', actionKey: 'merge', onConfirm: handleMergeEpisode },
    { id: 'generateAudio', title: 'Generate Audio', description: 'This will generate audio for all approved scripts. This process may take a few minutes depending on the number of clips.', confirmLabel: 'Generate Audio', loadingLabel: 'Generating...', actionKey: 'generate-audio', onConfirm: handleGenerateAudio },
    { id: 'retryAudio', title: 'Retry Audio Generation', description: 'This will retry audio generation for failed clips. Continue?', confirmLabel: 'Retry Audio', loadingLabel: 'Retrying...', actionKey: 'retry-failed-audio', onConfirm: handleRetryFailedAudio },
    { id: 'retryScripts', title: 'Retry Script Generation', description: 'This will retry script generation for failed clips. Continue?', confirmLabel: 'Retry Scripts', loadingLabel: 'Retrying...', actionKey: 'retry-failed-scripts', onConfirm: handleRetryFailedScripts },
    { id: 'retryAll', title: 'Retry All Failed', description: 'This will retry all failed operations for this episode. Continue?', confirmLabel: 'Retry All', loadingLabel: 'Retrying...', actionKey: 'retry-all-failed', onConfirm: handleRetryAllFailed },
    { id: 'unarchive', title: 'Unarchive Episode', description: 'This will restore the episode from the archive. Continue?', confirmLabel: 'Unarchive', loadingLabel: 'Unarchiving...', actionKey: 'unarchive', onConfirm: handleUnarchive },
  ];

  // Render mobile view for phone screens
  if (isMobile) {
    return <MobileEpisodeDetail />;
  }

  if (loading || clipsLoading) {
    return <EpisodeDetailSkeleton />;
  }

  if (error || !episode) {
    const isNotFound = error === 'Episode not found' || !episode;

    // Use animated EpisodeNotFound component for "not found" state
    if (isNotFound) {
      return <EpisodeNotFound onBack={() => navigate('../episodes')} />;
    }

    // Generic error state for other errors
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          {/* Icon */}
          <div className="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
            <AlertCircle className="h-10 w-10 text-destructive" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-medium text-foreground mb-2">
            Something Went Wrong
          </h1>

          {/* Description */}
          <p className="text-muted-foreground mb-8">{error}</p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('../episodes')} variant="default">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Episodes
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <>
      {/* Section 1: Episode Header Card */}
      <EpisodeHeaderCard
        episode={episode}
        clips={clips}
        artworkUrl={artworkUrl}
        isPublished={isPublished}
        isLockedByOther={isLockedByOther}
        lockHolder={lockHolder}
        onEditDetails={handleOpenEditDetailsDialog}
        onSaveDetails={handleSaveEpisodeDetails}
        onArchive={() => setActiveDialog('archive')}
        onDelete={() => setActiveDialog('delete')}
        onRefresh={() => { refreshEpisode(); loadClips(); }}
        getCompanyName={getCompanyName}
        isEditorExpanded={isEditorExpanded}
        onEditorExpandedChange={setIsEditorExpanded}
      />

      {/* Content wrapper - dims when inline editor is expanded */}
      <div
        className={cn(
          'transition-opacity duration-300',
          isEditorExpanded && 'opacity-30 pointer-events-none'
        )}
      >
      {/* Section 3: Action Bar - Full Bleed */}
      {/* Hidden during script/audio generation, merging, and publishing animations (but shown when all ready) */}
      {clips.length > 0 &&
        (episode.status !== EpisodeStatus.GENERATING_SCRIPTS || allScriptsReady) &&
        (episode.status !== EpisodeStatus.GENERATING_AUDIO || allAudioReady) &&
        episode.status !== EpisodeStatus.MERGING &&
        episode.status !== EpisodeStatus.PUBLISHING && (
        <div className="border-b border-border">
          <div className="max-w-content" style={{ paddingLeft: hPad, paddingRight: hPad }}>
            <ActionBar
            status={episode.status}
            onPrimaryAction={handlePrimaryAction}
            onSecondaryAction={handleSecondaryAction}
            onRefresh={() => { refreshEpisode(); loadClips(); }}
            onArchive={() => setActiveDialog('archive')}
            onDelete={() => setActiveDialog('delete')}
            isArchived={episode.status === EpisodeStatus.ARCHIVED}
            publishedUrl={episode.publishingProviderUrl}
            clipCount={clips.length}
            isLocked={isLockedByOther}
            // Playback controls - different handlers based on status
            isPlayingAll={
              episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW ? isPlayingAllClips :
              episode.status === EpisodeStatus.MERGED ? isPlayingMerged :
              isPlayingAll
            }
            isCurrentlyPlaying={
              episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW
                ? isPlayingAllClips && !isPlayAllClipsPaused
                : isPlayingAll && !isPlayAllPaused
            }
            canGoNext={
              episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW
                ? playAllClipsIndex !== null && playAllClipsIndex < clips.length - 1
                : playAllIndex !== null && !!episode?.mergePreview && playAllIndex < buildMergeSequence(clips, episode.mergePreview).length - 1
            }
            onPlayAll={
              episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW ? handlePlayAllClips :
              episode.status === EpisodeStatus.MERGED ? handlePlayMerged :
              handlePlayAll
            }
            onPausePlayAll={
              episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW ? handlePausePlayAllClips :
              episode.status === EpisodeStatus.MERGED ? handlePauseMerged :
              handlePausePlayAll
            }
            onResumePlayAll={episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW ? handleResumePlayAllClips : handleResumePlayAll}
            onNextClip={episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW ? handleNextClip : handleNextSegment}
            onStopPlayAll={episode.status === EpisodeStatus.AUDIO_PENDING_REVIEW ? handleStopPlayAllClips : handleStopPlayAll}
          />
          </div>
        </div>
      )}

      {/* Section 4: Main Content — only this area scrolls */}
      <div
        ref={contentAreaRef}
        className="overflow-y-auto max-w-content pb-8"
        style={{ paddingLeft: hPad, paddingRight: hPad, paddingTop: '24px', maxHeight: contentMaxHeight }}
      >
        {/* Feature Spotlight Banner */}
        <FeatureBanner contextTrigger="episode-composer:episode-detail" />

        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Status Banner - only show when clips exist and not during animations */}
        {/* Exception: show during generating_scripts/generating_audio if all scripts/audio are ready */}
        {clips.length > 0 &&
          (episode.status !== EpisodeStatus.GENERATING_SCRIPTS || allScriptsReady) &&
          (episode.status !== EpisodeStatus.GENERATING_AUDIO || allAudioReady) &&
          episode.status !== EpisodeStatus.MERGING &&
          episode.status !== EpisodeStatus.PUBLISHING && (
          <StatusBanner
            status={episode.status}
            clips={clips}
            publishedAt={episode.publishedAt}
            errorMessage={episode.errorMessage}
          />
        )}

        {/* Content Area - Status-based rendering */}
        <div className="borderx rounded-lg p-0 mt-6">
          <EpisodeStatusContent
            episode={episode}
            clips={clips}
            clipsLoading={clipsLoading}
            actionInProgress={actionInProgress}
            refreshEpisode={refreshEpisode}
            loadClips={loadClips}
            handleMoveClip={handleMoveClip}
            handleDeleteClip={handleDeleteClip}
            handleRegenerateScript={handleRegenerateScript}
            handleRegenerateAudio={handleRegenerateAudio}
            saveScriptForClip={saveScriptForClip}
            onAddArticles={() => setShowArticleSelector(true)}
            currentlyPlayingId={currentlyPlayingId}
            isPlayingAll={isPlayingAll}
            playAllIndex={playAllIndex}
            isPlayAllPaused={isPlayAllPaused}
            isPlayingAllClips={isPlayingAllClips}
            playAllClipsIndex={playAllClipsIndex}
            isPlayAllClipsPaused={isPlayAllClipsPaused}
            isPlayingMerged={isPlayingMerged}
            setIsPlayingMerged={setIsPlayingMerged}
            handlePlayAll={handlePlayAll}
            handleStopPlayAll={handleStopPlayAll}
            handlePlayStateChange={handlePlayStateChange}
            handleSegmentEnded={handleSegmentEnded}
            handleClipEnded={handleClipEnded}
            mergedAudioUrl={mergedAudioUrl}
            mergedAudioLoading={mergedAudioLoading}
            mergedAudioError={mergedAudioError}
            mergedAudioRef={mergedAudioRef}
            totalDuration={totalDuration}
            isLockedByOther={isLockedByOther}
          />
        </div>
      </div>
      </div>
      {/* End content wrapper */}

      {/* Article Selector Modal */}
      <ArticleSelectorModal
        open={showArticleSelector}
        onOpenChange={setShowArticleSelector}
        episodeId={episode.id}
        companyId={episode.companyId}
        onArticlesAdded={handleArticlesAdded}
        existingClipCount={clips.length}
        existingClips={clips}
      />

      {/* Confirmation Dialogs — rendered from config */}
      {dialogConfigs.map((config) => (
        <ConfirmationDialog
          key={config.id}
          config={config}
          open={activeDialog === config.id}
          onOpenChange={(open) => { if (!open) setActiveDialog(null); }}
          actionInProgress={actionInProgress}
        />
      ))}

      {/* Publish Episode Dialog */}
      <PublishEpisodeDialog
        open={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        onPublish={handlePublish}
        isLoading={actionInProgress === 'publish'}
        hasDescription={!!episode?.description?.trim()}
        timePresets={publishTimePresets}
      />

      {/* Edit Episode Details Dialog */}
      <EditEpisodeDetailsDialog
        open={showEditDetailsDialog}
        onOpenChange={setShowEditDetailsDialog}
        episode={episode}
        clips={clips}
        onSave={handleSaveEpisodeDetails}
      />
    </>
  );
};
